import {useCallback, useMemo, useRef, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import {useAuth} from 'UI/hooks/useAuth';
import {useGeolocation} from 'UI/hooks/useGeolocation';
import {useLocationSetting} from 'UI/hooks/useLocationSetting';

import {getDistanceInMeters, pointToLatLng} from 'UI/utils/geo';
import type {LatLng} from 'UI/utils/geo';

/** Number of nearby waypoints fetched per page, and revealed per "load more" click. */
export const NEARBY_WAYPOINTS_PAGE_SIZE = 5;

/**
 * Minimum movement (in meters) required before a new GPS fix is treated as an actual position
 * change. `watchPosition` fires continuously with small jitter even while standing still —
 * without this threshold, every tiny fluctuation would re-sort/re-fetch the list, making it
 * feel like it constantly jumps around on mobile.
 */
const MOVE_THRESHOLD_METERS = 25;

const METERS_PER_KM = 1000;
const METER_ROUNDING = 10;
const KM_DECIMALS = 1;

/** Raw waypoint data as returned by `/api/waypoints/nearby`, already sorted nearest-first by MongoDB. */
interface NearbyWaypointDoc {
    /** Unique waypoint ID. */
    id: string;
    /** Alt text for `imageUrl`, if any. */
    imageAlt: string | null;
    /** URL of the waypoint's main image, if any. */
    imageUrl: string | null;
    /** GeoJSON-style `[longitude, latitude]` location. */
    location: [number, number];
    /** Short description, if any. */
    shortDescription: string | null;
    /** 1-based order of this waypoint within its walk. */
    sortOrder: number;
    /** Waypoint title. */
    title: string;
    /** ID of the waypoint's walk, used to check library ownership. */
    walkId: string;
    /** Slug of the waypoint's walk. */
    walkSlug: string;
    /** Title of the waypoint's walk. */
    walkTitle: string;
}

/** The response shape of one page of `/api/waypoints/nearby` — `@nfq/typed-next-api` sends the controller's `data` payload as the response body directly, unwrapped. */
interface NearbyWaypointsResponse {
    docs: NearbyWaypointDoc[];
    /** Whether more results exist beyond this page — computed server-side from the exact candidate count. */
    hasMore: boolean;
}

/** A nearby waypoint enriched with the distance to the user's current position, ready for display. */
export interface NearbyWaypointItem {
    /** The waypoint's short description. */
    description?: string;
    /** Distance to the user, formatted for display (e.g. `"850 m"`). */
    distanceLabel: string;
    /** Link URL — straight to this waypoint if the walk is already in the user's library, otherwise to the walk overview. */
    href: string;
    /** Unique waypoint ID. */
    id: string;
    /** Alt text for `imageUrl`. */
    imageAlt?: string;
    /** URL of the waypoint's main image. */
    imageUrl?: string;
    /** 1-based order of this waypoint within its walk. */
    number: number;
    /** The waypoint title. */
    title: string;
    /** Title of the walk this waypoint belongs to. */
    walkTitle: string;
}

/**
 * Fetches one page of nearby waypoints from the API.
 *
 * @param url The request URL, including query params.
 * @returns The parsed JSON response.
 */
const fetchNearbyWaypoints = async (url: string): Promise<NearbyWaypointsResponse> => {
    const res = await fetch(url);

    return res.json() as Promise<NearbyWaypointsResponse>;
};

/**
 * Formats a distance in meters as a short, localized label — meters below 1 km, one decimal
 * kilometer above.
 *
 * @param meters The distance in meters.
 * @param t      The i18next translation function.
 * @returns A formatted distance label, e.g. `"850 m"` or `"1.2 km"`.
 */
const formatDistance = (meters: number, t: ReturnType<typeof useTranslation>['t']): string => {
    if (meters < METERS_PER_KM) {
        const rounded = Math.round(meters / METER_ROUNDING) * METER_ROUNDING;

        return `${rounded} ${t('common.meters')}`;
    }

    return `${(meters / METERS_PER_KM).toFixed(KM_DECIMALS)} ${t('common.km')}`;
};

/**
 * Hook that finds the waypoints nearest to the user's current location. By default only the
 * first waypoint of each published walk is searched; toggling `showAllWaypoints` switches to
 * searching every waypoint of every published walk instead. The actual proximity search and
 * sorting is done by MongoDB's geospatial `near` query (via `/api/waypoints/nearby`), fetched
 * page by page with SWR's infinite-loading helper — each `loadMore` call fetches exactly one
 * more page of `NEARBY_WAYPOINTS_PAGE_SIZE` waypoints and appends it, it never re-fetches
 * earlier pages. Only fetches once the user has enabled location sharing and a position fix is
 * available.
 *
 * Re-querying the server (which can reorder or replace list items) only happens once the user
 * has moved `MOVE_THRESHOLD_METERS`. Between such re-queries, the list's membership and order
 * stay untouched — only each item's distance label is recomputed from the live (unthrottled) GPS
 * position, so the "X m away" text keeps ticking smoothly without the list itself reshuffling.
 *
 * @returns The currently loaded nearest waypoints enriched with a distance label, whether more
 * results are available, a `loadMore` handler, the current search mode and a setter for it, the
 * translation function, whether a fetch is in flight, and whether the section should be shown at all.
 */
export const useNearbyWaypoints = () => {
    const {t} = useTranslation();
    const {locale} = useRouter();
    const {isInLibrary} = useAuth();
    const {isEnabled: isLocationEnabled} = useLocationSetting();
    const {position: rawPosition} = useGeolocation(isLocationEnabled);
    const [showAllWaypoints, setShowAllWaypoints] = useState(false);

    /*
     * Throttles the constantly-jittering GPS position down to a "stable" one that only moves
     * once the user has actually walked `MOVE_THRESHOLD_METERS`. Adjusting state directly during
     * render (rather than in an effect) applies the new stable position immediately, in the same
     * render pass — see https://react.dev/reference/react/useState#storing-information-from-previous-renders.
     */
    const [position, setPosition] = useState<LatLng | null>(null);
    const lastMovePositionRef = useRef<LatLng | null>(null);

    if (rawPosition) {
        const next: LatLng = {
            lat: rawPosition.latitude,
            lng: rawPosition.longitude
        };

        const hasMovedEnough = !lastMovePositionRef.current
            || getDistanceInMeters(lastMovePositionRef.current, next) >= MOVE_THRESHOLD_METERS;

        if (hasMovedEnough) {
            lastMovePositionRef.current = next;

            if (position?.lat !== next.lat || position.lng !== next.lng) {
                setPosition(next);
            }
        }
    } else if (position) {
        lastMovePositionRef.current = null;
        setPosition(null);
    }

    /**
     * Builds the SWR key (request URL) for one page, or `null` to stop fetching further pages.
     *
     * @param pageIndex        0-based index of the page to fetch.
     * @param previousPageData The previous page's response, or `null` for the first page.
     * @returns The request URL for this page, or `null` if there's no position yet or the previous page reported no more results.
     */
    const getKey = (pageIndex: number, previousPageData: NearbyWaypointsResponse | null): string | null => {
        if (!position) return null;
        if (previousPageData && !previousPageData.hasMore) return null;

        const params = new URLSearchParams({
            lat: String(position.lat),
            limit: String(NEARBY_WAYPOINTS_PAGE_SIZE),
            lng: String(position.lng),
            mode: showAllWaypoints ? 'all' : 'start',
            page: String(pageIndex + 1)
        });

        if (locale) params.set('locale', locale);

        return `/api/waypoints/nearby?${params.toString()}`;
    };

    const {data, isValidating, setSize} = useSWRInfinite<NearbyWaypointsResponse>(getKey, fetchNearbyWaypoints);

    const hasMore = data?.[data.length - 1]?.hasMore ?? false;

    /*
     * The list's membership and order only change when `data` changes — i.e. only once the user
     * has crossed `MOVE_THRESHOLD_METERS` and a real re-query happened, or a new page was loaded.
     * Kept as its own memo so the `waypoints` memo below can depend on the live `rawPosition`
     * for the distance label without also churning `docs`' identity on every GPS tick.
     */
    const docs = useMemo(() => data?.flatMap(page => page.docs) ?? [], [data]);

    const waypoints = useMemo((): NearbyWaypointItem[] => {
        if (!rawPosition) return [];

        const userLatLng = {
            lat: rawPosition.latitude,
            lng: rawPosition.longitude
        };

        return docs.map((doc): NearbyWaypointItem => ({
            description: doc.shortDescription ?? undefined,
            distanceLabel: formatDistance(getDistanceInMeters(userLatLng, pointToLatLng(doc.location)), t),
            href: isInLibrary(doc.walkId) ? `/walks/${doc.walkSlug}/start/${doc.sortOrder}` : `/walks/${doc.walkSlug}`,
            id: doc.id,
            imageAlt: doc.imageAlt ?? undefined,
            imageUrl: doc.imageUrl ?? undefined,
            number: doc.sortOrder,
            title: doc.title,
            walkTitle: doc.walkTitle
        }));
    }, [docs, isInLibrary, rawPosition, t]);

    const loadMore = useCallback(() => {
        void setSize(currentSize => currentSize + 1);
    }, [setSize]);

    /**
     * Switches between searching only the first waypoint of each walk and searching all
     * waypoints, resetting pagination back to the first page.
     *
     * @param value Whether to search all waypoints instead of only the first one of each walk.
     */
    const handleShowAllWaypointsChange = useCallback((value: boolean) => {
        setShowAllWaypoints(value);
        void setSize(1);
    }, [setSize]);

    return {
        hasMore,
        isLoading: isValidating,
        isVisible: Boolean(position),
        loadMore,
        setShowAllWaypoints: handleShowAllWaypointsChange,
        showAllWaypoints,
        t,
        waypoints
    };
};